import {Injectable} from '@angular/core';
import {ProductRepository} from '../repositories/product.repository';
import {MbDatatablesTemplate} from '../components/mb-datatable/mb-datatables-template';
import {Constants} from '../utils/constants';
import {FilterService} from './filterer/filter.service';
import {Dtos} from '../utils/dtos';
import {BaseService} from './common/base.service';
import {DataManipulationService} from './common/data-manipulation.service';
import {ValidatorService} from './common/validator.service';
import {MbColDefinition, MbColInput} from '../components/mb-datatable/models/mb-datatable.model';
import {CoreDataService} from './wrapper/core-data.service';
import {Utilities} from "../utils/utilities";

@Injectable({
    providedIn: 'root'
})
export class ProductService extends BaseService<ProductRepository> {


    constructor(repo: ProductRepository, filterService: FilterService, dataManipulationService: DataManipulationService, validatorService: ValidatorService,
                private coreDataService: CoreDataService) {
        super(repo, filterService, dataManipulationService, validatorService);
        this.typeName = 'Product';
    }

    public getAllForRestaurant(restaurantHistoryId: number) {
        return this.repo.getAllForRestaurant(restaurantHistoryId);
    }


    public async getAllShopGiftCardForRestaurant(restaurantHistoryId: number): Promise<any[]> {
        let products = await this.getAllSlimGiftCard().toPromise();
        products = products.filter(product => {
            return (
                product.restaurants.find(restaurant => restaurant.historyId == restaurantHistoryId) &&
                product.giftCard &&
                product.giftCard.optionMap &&
                product.giftCard.optionMap.RESTAURANT &&
                product.giftCard.optionMap.RESTAURANT.optionPackagesWithAndOperation &&
                Object.keys(product.giftCard.optionMap.RESTAURANT.optionPackagesWithAndOperation).length > 0
            );

        });
        return Utilities.createMultiSelectElements(products, 'id', 'name');
    }
    public async getAllDeliveryGiftCardForRestaurant(restaurantHistoryId: number): Promise<any[]> {
        let products = await this.getAllSlimGiftCard().toPromise();
        products = products.filter(product => {
            return (
                product.restaurants.find(restaurant => restaurant.historyId == restaurantHistoryId) &&
                product.giftCard &&
                product.giftCard.optionMap &&
                product.giftCard.optionMap.DELIVERY &&
                product.giftCard.optionMap.DELIVERY.optionPackagesWithAndOperation &&
                Object.keys(product.giftCard.optionMap.DELIVERY.optionPackagesWithAndOperation).length > 0
            );

        });
        return Utilities.createMultiSelectElements(products, 'id', 'name');
    }

    public getAllProductByType(type: string) {
        const filter = this.filterService.filter(Dtos.PRODUCT.ProductForDashboardListDTO)
            .equal('type', type)
            .and()
            .notEqual('deleted', true)
            .and()
            .notEqual('status', 'DEPRECATED')
            .create();

        return this.repo.getAllByFilter(filter);
    }

    public getAllSlimGiftCard() {
        const filter = this.filterService.filter(Dtos.PRODUCT.LocalizedProductDTO)
            .equal('type', 'GIFT_CARD')
            .and()
            .equal('deleted', false)
            .and()
            .equal('status', 'PUBLISHED')
            .create();

        return this.repo.getAllByFilter(filter);
    }

    public getAllForOrder() {
        return this.getAllSlimGiftCard();
    }

    public filterOrderProductsForGiftCardSelect(products: any[]) {
        const result = [];
        products.forEach(product => {
            if (product.productType == 'GIFT_CARD') {
                result.push(
                    {
                        id: product.id,
                        itemName: product.name,
                        minPurchaseAmount: product.giftCard.minPurchaseAmount,
                        entity: product,
                        isEvent: false
                    }
                );
            }
        });
        return result;
    }

    public filterOrderProductsByRestaurantHistoryId(products: any[], restaurantHistoryId: number) {
        const result = [];
        products.forEach(product => {
            const found = product.restaurants.find(element => element.historyId == restaurantHistoryId);
            if (found != undefined) {
                result.push(product);
            }
        });
        return result;
    }

    public getAllPackageProduct(isEvent: boolean) {
        let filter = this.filterService.filter(Dtos.PRODUCT.LocalizationsAndMinPurchaseAmountProductDTO)
            .notEqual('type', 'GIFT_CARD')
            .and()
            .notEqual('type', 'EVENT')
            .and()
            .notEqual('type', 'PACKAGE')
            .and()
            .notEqual('type', 'DIY_AT_HOME')
            .and()
            .notEqual('type', 'ADDITIONAL')
            .and()
            .notEqual('deleted', true)
            .and()
            .equal('status', 'PUBLISHED')
            .create();

        if (isEvent) {
            filter = this.filterService.filter(Dtos.PRODUCT.LocalizationsAndMinPurchaseAmountProductDTO)
                .notEqual('type', 'GIFT_CARD')
                .and()
                .notEqual('type', 'EVENT')
                .and()
                .notEqual('type', 'DIY_AT_HOME')
                .and()
                .notEqual('type', 'ADDITIONAL')
                .and()
                .notEqual('deleted', true)
                .and()
                .equal('status', 'PUBLISHED')
                .create();
        }

        return this.repo.getAllByFilter(filter);
    }

    public getAllOptionable() {
        const filter = this.filterService.filter(Dtos.PRODUCT.LocalizationsAndMinPurchaseAmountProductDTO)
            .equal('type', 'DRINK')
            .or()
            .equal('type', 'PRODUCT')
            .or()
            .equal('type', 'FOOD')
            .or()
            .equal('type', 'PACKAGE')
            .or()
            .equal('type', 'EXTRA')
            .and()
            .notEqual('deleted', true)
            .and()
            .equal('status', 'PUBLISHED')
            .create();

        return this.repo.getAllByFilter(filter);
    }

    protected defineModelSpecificColumns(extraInfo?: any): any[] {
        return [
            MbColDefinition.new('name', 'product.name'),
            MbColDefinition.new('huf', 'product.huf'),
            MbColDefinition.new('status', 'product.status', MbColInput.new(MbColInput.SELECT, Constants.HISTORY_STATUS_OPTIONS), MbDatatablesTemplate.TRANSLATE_TEMPLATE),
            MbColDefinition.new('productType', 'product.productType', MbColInput.new(MbColInput.SELECT, extraInfo), MbDatatablesTemplate.TRANSLATE_TEMPLATE),
            MbColDefinition.new('modifier.email', 'base.modifier'),
        ];
    }

    saveOrUpdate(model: BaseProduct) {
        if (model.id == null) {
            return this.save(model);
        } else {
            return this.update(model);
        }
    }

    publish(id: number) {
        return this.repo.publish(id);
    }

    revoke(id: number) {
        return this.repo.revoke(id);
    }

    copy(product: BaseProduct) {
        const copied = {...product};
        copied.id = null;
        return copied;
    }

    patchToRestaurantHistory(productId: number, restaurantHistoryIds: number[]) {
        const payload = {
            id: productId,
            type: 'PRODUCT_RESTAURANTS',
            attribute: restaurantHistoryIds
        };
        return this.repo.patchToRestaurantHistory(payload);
    }

    validate(model: BaseProduct) {
        this.validatorService.backendMapIsEmpty(model.attachments, 'product.error.notChooseAttachment');
        this.validatorService.backendMapIsEmpty(model.prices, 'product.error.notSetPrices');
        const pricesKey = Object.keys(model.prices);
        if (this.coreDataService.getCurrencies().length > pricesKey.length) {
            throw new Error('product.error.notSetEveryCurrencies');
        }
        Object.values<any>(model.prices).forEach(price => {
            if (price.price < 1) {
                throw new Error('product.error.priceIs0');
            }
        });
        this.validatorService.validateLocalizations(model.localizedProduct, ['name', 'description', 'title']);
        if (model.productType == 'GIFT_CARD') {
            if (model.giftCard == null) {
                throw new Error('product.error.notSetGiftCard');
            } else {
                if (!model.giftCard.isValid()) {
                    throw new Error('product.error.notSetValidGiftCard');
                }
            }
        }
        if (model.productType == 'PRODUCT') {
            if (model.stockProduct == null) {
                throw new Error('product.error.notSetGiftCard');
            } else {
                if (!model.stockProduct.isValid()) {
                    throw new Error('product.error.notSetValidGiftCard');
                }
            }
        }
        if (model.productType == 'DRINK') {
            this.validatorService.required(model.drinkCategory, 'product.error.notSetDrinkCategory');
        }
        if (model.productType == 'FOOD') {
            this.validatorService.required(model.diyType, 'product.error.notSetDiyType');
        }
        // if (model.productType == "PACKAGE") {
        //     this.validatorService.required(model.packageProducts, 'product.error.notSetPackageProducts');
        //     if(model.packageProducts.length<1){
        //         throw new Error('product.error.notSetPackageProducts');
        //     }
        // }
        // if (model.productType == "EVENT") {
        //     this.validatorService.required(model.drinkCategory, 'product.error.notSetPackageProducts');
        // }
        if (model.restaurantHistoryIds.length == 0) {
            throw new Error('product.error.notSetRestaurantConnection');
        }

    }

}

export class GiftCardOptionPackage {
    productsWithOrOperationIds: number[];

    constructor() {
        this.productsWithOrOperationIds = [];
    }
}

export class BaseGiftCard {
    personCount: number;
    minPurchaseAmount: number;
    validityInDay: number;
    optionMap: any;

    constructor(personCount?: number, minPurchaseAmount?: number, validityInDay?: number, optionMap?: any) {
        this.personCount = personCount;
        this.minPurchaseAmount = minPurchaseAmount;
        this.validityInDay = validityInDay;
        this.optionMap = optionMap;
    }

    isValid() {
        return this.personCount && this.personCount > 0 && this.minPurchaseAmount && this.minPurchaseAmount > 0 && this.validityInDay && this.validityInDay > 0;
    }
}

export class ProductPrice {
    price: number;
    vat: number;

    constructor(price?: number, vat?: number) {
        this.price = price;
        this.vat = vat;
    }
}

export class ProductLocalization {
    name: string;
    title: string;
    description: string;
    slug: string;

    constructor(name?: string, title?: string, description?: string) {
        this.name = name;
        this.title = title;
        this.description = description;
    }

    isValid(): boolean {
        return this.name && this.name.length > 1 && this.title && this.title.length > 1 && this.description && this.description.length > 1;
    }
}

export class BaseStockProduct {
    inStock: number;

    constructor(inStock: number) {
        this.inStock = inStock;
    }

    isValid() {
        return this.inStock > 0;
    }
}

export class BasePackageProduct {
    childProduct: BaseProduct = null;

    constructor(childProduct: BaseProduct) {
        this.childProduct = childProduct;
    }
}

export class BaseProduct {
    id: number = null;
    productType: string;
    independentlySold = false;
    localizedProduct: any;
    prices: any;
    attachments: any;
    giftCard: BaseGiftCard = null;
    diyType: any = null;
    drinkCategory: any = null;
    packageProducts: BasePackageProduct[] = null;
    stockProduct: BaseStockProduct = null;
    localizations: any;
    restaurantHistoryIds: number[];
    isHistory = false;

    constructor() {
        this.localizedProduct = {};
        this.prices = {};
        this.attachments = {};
        this.packageProducts = [];
        this.restaurantHistoryIds = [];
    }
}

export class ProductTableTypeAndOption {
    static GIFT_CARD = [{label: 'enum.GIFT_CARD', value: 'GIFT_CARD'}];
    static PRODUCT = [{label: 'enum.PRODUCT', value: 'PRODUCT'}];
    static DRINK = [{label: 'enum.DRINK', value: 'DRINK'}];
    static EXTRA = [{label: 'enum.EXTRA', value: 'EXTRA'}];
    static PACKAGE = [{label: 'enum.PACKAGE', value: 'PACKAGE'}];
    static FOOD = [{label: 'enum.FOOD', value: 'FOOD'}];
    static EVENT = [{label: 'enum.EVENT', value: 'EVENT'}];
    static SPECIAL_EVENT = [{label: 'product.specialEvent', value: 'SPECIAL_EVENT'}];
}
